import React from 'react';
import { Link } from 'react-router-dom';
import type { Components } from 'react-markdown';
import { Callout } from '../components/Callout';
import { HelpScreenshot } from '../components/HelpScreenshot';
import type { CalloutKind } from './alerts';
import { resolveImage } from './resolveImage';
import { resolveLink } from './resolveLink';
import { getGuide } from '../registry';

const LINK =
  'font-medium text-blue-600 underline-offset-2 hover:underline dark:text-blue-400';
const BODY = 'text-gray-700 dark:text-gray-300';

export interface ComponentOptions {
  onNavigateGuide: (id: string) => void;
}

export function buildComponents({
  onNavigateGuide,
}: ComponentOptions): Components {
  return {
    h1: () => null,
    h2: ({ id, children }) => (
      <h2
        id={id}
        className="mb-3 mt-8 scroll-mt-24 border-b border-gray-200 pb-2 text-lg font-semibold text-title-page-color-100 first:mt-0 dark:border-gray-700 dark:text-white"
      >
        {children}
      </h2>
    ),
    h3: ({ id, children }) => (
      <h3
        id={id}
        className="mb-2 mt-6 scroll-mt-24 text-base font-semibold text-title-page-color-100 dark:text-white"
      >
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="mb-1 mt-4 text-sm font-semibold text-title-page-color-100 dark:text-gray-200">
        {children}
      </h4>
    ),
    p: ({ children }) => (
      <p className={`my-3 text-sm leading-6 ${BODY}`}>{children}</p>
    ),
    ul: ({ children }) => (
      <ul
        className={`my-3 list-disc space-y-1.5 pl-6 text-sm leading-6 ${BODY}`}
      >
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol
        className={`my-3 list-decimal space-y-2 pl-6 text-sm leading-6 ${BODY}`}
      >
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="pl-1 marker:font-semibold marker:text-blue-600 dark:marker:text-blue-400">
        {children}
      </li>
    ),
    strong: ({ children }) => (
      <strong className="font-semibold text-gray-900 dark:text-white">
        {children}
      </strong>
    ),
    hr: () => <hr className="my-6 border-gray-200 dark:border-gray-700" />,
    blockquote: ({ children }) => (
      <blockquote className="my-4 border-l-4 border-gray-300 pl-4 italic text-gray-600 dark:border-gray-600 dark:text-gray-400">
        {children}
      </blockquote>
    ),
    table: ({ children }) => (
      <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="w-full text-left text-sm">{children}</table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-800 dark:text-gray-400">
        {children}
      </thead>
    ),
    th: ({ children }) => (
      <th className="px-3 py-2 font-semibold">{children}</th>
    ),
    td: ({ children }) => (
      <td
        className={`border-t border-gray-100 px-3 py-2 align-top dark:border-gray-700 ${BODY}`}
      >
        {children}
      </td>
    ),
    code: ({ inline, children }) =>
      inline ? (
        <kbd className="rounded border border-gray-300 bg-gray-100 px-1.5 py-0.5 font-sans text-[0.9em] font-medium text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100">
          {children}
        </kbd>
      ) : (
        <code className="block overflow-x-auto rounded-lg bg-gray-900 p-3 font-mono text-xs text-gray-100 dark:bg-black">
          {children}
        </code>
      ),
    pre: ({ children }) => <pre className="my-3">{children}</pre>,
    a: ({ href, children }) => {
      const resolved = resolveLink(href ?? '');
      switch (resolved.kind) {
        case 'guide':
          if (!getGuide(resolved.id)) return <span>{children}</span>;
          return (
            <button
              type="button"
              className={LINK}
              onClick={() => onNavigateGuide(resolved.id)}
            >
              {children}
            </button>
          );
        case 'app':
          return (
            <Link to={resolved.to} className={LINK}>
              {children}
            </Link>
          );
        case 'anchor':
          return (
            <a href={resolved.href} className={LINK}>
              {children}
            </a>
          );
        default:
          return (
            <a
              href={resolved.href}
              className={LINK}
              target="_blank"
              rel="noopener noreferrer"
            >
              {children}
            </a>
          );
      }
    },
    img: ({ src, alt }) => {
      const resolved = resolveImage(src ?? '');
      if (resolved.kind === 'screenshot') {
        return <HelpScreenshot caption={alt ?? ''} screenshotKey={resolved.key} />;
      }
      return (
        <img
          src={resolved.src}
          alt={alt ?? ''}
          className="my-5 w-full rounded-xl border border-gray-200 dark:border-gray-700"
        />
      );
    },
    div: ({ node: _node, children, ...props }) => {
      const kind = (props as Record<string, unknown>)['data-callout'] as
        | CalloutKind
        | undefined;
      if (kind) return <Callout kind={kind}>{children}</Callout>;
      return <div>{children}</div>;
    },
  };
}
